import React from "react"
import { useSelector } from "react-redux" // Ensure correct import for useSelector
import { IoPersonCircleOutline } from "react-icons/io5"
import ReactMarkdown from "react-markdown"
import {
	convertMinutesTo12HHMM,
	convertMinutesToHoursAndMinutesString,
	timeStringToMinutes
} from "../../utils/generalUtils"
import { endTimeAllCases, getStartTime } from "../../utils/realTimeUtils"
import {
	getScheduleSelection,
	getScheduleForSelection
} from "../DailyCalendarFiles/CustomCalendar/scheduleSelection"

const CustomTooltipContent = ({ task, nurse }) => {
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const chairSchedule = useSelector(state => state.schedule.chairSchedule)
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)
	const currentSchedule = getScheduleForSelection(scheduleSelection)
	const closeTime = timeStringToMinutes(currentSchedule.closeTime)

	const chairName =
		chairSchedule.find(chair => chair.chairId === task.assignedChair)?.chairName ||
		"Unknown Chair"
	const chairType = chairSchedule.find(chair => chair.chairId === task.assignedChair)?.chairType
		? Object.keys(
				chairSchedule.find(chair => chair.chairId === task.assignedChair)?.chairType
			).find(
				type =>
					chairSchedule.find(chair => chair.chairId === task.assignedChair)?.chairType[
						type
					]
			) || "Unknown Type"
		: "Unknown Type"

	const expectedEndTime = endTimeAllCases(task, sandbox, isTomorrowMode)

	// Calculate expected end time and delay message
	let delayMessage = ""
	if (expectedEndTime > closeTime) {
		const minutesAfterClose = expectedEndTime - closeTime
		if (minutesAfterClose > 0) {
			delayMessage = `- ${task.patientName} is expected to end at **${convertMinutesTo12HHMM(expectedEndTime)}**, which is **${convertMinutesToHoursAndMinutesString(minutesAfterClose)}** after center close time.`
		}
	}

	const reasonForVisit = task?.reasonForVisit ? `- ${task?.reasonForVisit}` : ""

	return (
		<div className="bg-white rounded-lg shadow-lg p-4 text-sm text-gray-800 w-72">
			<div className="flex items-center text-customBlue font-semibold mb-4">
				<IoPersonCircleOutline className="mr-2" /> <span>Summary</span>
			</div>
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Name</div>
				<div className="sensitive font-semibold text-gray-900">{task.patientName}</div>
			</div>
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Type</div>
				<div className="font-semibold text-gray-900">{task.infusionType}</div>
			</div>
			{task.clinicAppointmentTime !== undefined && (
				<div className="mb-2 flex justify-between">
					<div className="text-gray-500">Clinic Appointment</div>
					<div className="font-semibold text-gray-900">
						{task.clinicAppointmentTime
							? convertMinutesTo12HHMM(task.clinicAppointmentTime)
							: "N/A"}
					</div>
				</div>
			)}
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Scheduled Time</div>
				<div className="font-semibold text-gray-900">
					{convertMinutesTo12HHMM(task.scheduledStartTime) || ""} -{" "}
					{convertMinutesTo12HHMM(task.scheduledEndTime) || ""}
				</div>
			</div>
			{task.actualStartTime && (
				<div className="mb-2 flex justify-between">
					<div className="text-gray-500">Actual Time</div>
					<div className="font-semibold text-gray-900">
						{convertMinutesTo12HHMM(getStartTime(task, sandbox, isTomorrowMode))} -{" "}
						{task.actualEndTime
							? convertMinutesTo12HHMM(task.actualEndTime)
							: "Ongoing"}
					</div>
				</div>
			)}
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Status</div>
				<div className="font-semibold text-gray-900">{task.internalStatus}</div>
			</div>
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Chair</div>
				<div className="font-semibold text-gray-900">{`${chairName} (${chairType})`}</div>
			</div>
			{task.transferredBy !== undefined &&
				task.transferredBy !== null &&
				task.transferredBy !== nurse && (
					<div className="mb-2 flex justify-between">
						<div className="text-gray-500">Original Nurse</div>
						<div className="font-semibold text-gray-900">{`${nurseSchedule.find(nurse => nurse.nurseId === task.transferredBy)?.nurseName || "Unknown Nurse"}`}</div>
					</div>
				)}
			<div className="mb-2 flex justify-between">
				<div className="text-gray-500">Acuity Score</div>
				<div className="font-semibold text-gray-900">{task.acuity}</div>
			</div>
			<hr className="my-2" />
			<div className="mt-2 text-left">
				<div className="sensitive text-gray-500">Notes</div>
				<div className="prose prose-slate prose-sm text-black prose-li:text-black">
					<ReactMarkdown>{task.notes}</ReactMarkdown>
				</div>
				<div className="sensitive prose prose-slate prose-sm text-black prose-li:text-black">
					<ReactMarkdown>{reasonForVisit}</ReactMarkdown>
				</div>
				<div className="sensitive prose prose-slate prose-sm text-black prose-li:text-black">
					<ReactMarkdown>{delayMessage}</ReactMarkdown>
				</div>
			</div>
		</div>
	)
}

export default CustomTooltipContent
